import React, {createContext, useContext, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import server, {getAccessToken, getRefreshToken, setTokens, clearTokens} from '../global/server';
import {useApp} from '../context/AppContext';
import {mutate as swrMutate} from 'swr';

const AuthContext = createContext({});

export const AuthProvider = props => {
    // Comeca carregando: enquanto a sessao nao for verificada nao da para
    // afirmar que o usuario esta deslogado, senao a tela de login pisca.
    const [loadingAuth, setLoading] = useState(true);
    // Só a primeira verificação segura a tela. Depois dela o ProtectedLayout
    // nao pode mais ficar em branco: ele desmonta a arvore de rotas inteira e
    // leva junto o estado local de quem estiver na tela (modal aberto, form
    // preenchido, passo de wizard). Revalidacao roda em segundo plano.
    const sessaoJaVerificada = useRef(false);
    const navigate = useNavigate();
    const [rules, setRules] = useState();
    const [token, setToken] = useState();
    const [user, setUser] = useState({});

    const globalParams = async () => {
        try {
            const {data} = await server.get('/auth/publicRules');
            if (data && data.data) {
                setRules(data.data);
                const current_version = localStorage.getItem('current_version');

                if (!Boolean(current_version) || current_version !== data.data.front_version) {
                    localStorage.setItem('current_version', data.data.front_version);
                    window.location.reload();
                }
            }
        } catch (error) {
            console.log(error);
        }
    };

    const verifyAccount = async () => {
        const {data} = await server.get('account');
        setUser(data.data);
    };

    const verifySession = async () => {
        const primeiraVerificacao = !sessaoJaVerificada.current;
        try {
            if (primeiraVerificacao) setLoading(true);

            // Basta ter o refresh token: o access token vale 15 min e o
            // interceptor do axios renova sozinho na primeira chamada.
            if (!Boolean(getAccessToken()) && !Boolean(getRefreshToken())) throw 'Not session';

            await verifyAccount();
            setToken(getAccessToken());
        } catch (error) {
            // So descarta a sessao quando ela realmente morreu. Falha de rede
            // nao pode apagar cookie valido nem derrubar quem ja esta logado,
            // senao uma instabilidade do servidor desloga todo mundo de uma vez.
            if (error === 'Not session' || error?.response?.status === 401) {
                clearTokens();
                setToken();
            }
        } finally {
            sessaoJaVerificada.current = true;
            if (primeiraVerificacao) setLoading(false);
        }
    };

    useEffect(() => {
        verifySession();
        globalParams();
    }, []);

    /**
     * Recebe o bloco `data` da resposta de login: {token, refresh_token, user}.
     */
    const Login = (session, path) => {
        setLoading(true);
        setTokens(session, path || '/');
        setToken(session.token);
        setUser(session.user);
        setLoading(false);
    };

    const LogOut = async () => {
        const refresh_token = getRefreshToken();

        setToken();
        setUser({});
        clearTokens();
        // Limpa todo o cache do SWR para evitar dados de outro usuario
        swrMutate(() => true, undefined, { revalidate: false });

        // Revoga a sessao no servidor. Sem isso o refresh token continuaria
        // valido por 6 meses mesmo depois de o usuario sair.
        if (refresh_token) {
            try {
                await server.post('/auth/logout', {refresh_token});
            } catch {
                // Sair da conta no navegador nao pode depender do servidor
            }
        }
    };

    return (
        <AuthContext.Provider
            value={{
                logged: Boolean(token),
                user,
                rules,
                loading: loadingAuth,
                LogOut,
                Login,
                verifySession,
                setUser,
            }}>
            {props.children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw 'Use Auth Context inside Auth Provider';

    return context;
};
